import { Injectable } from '@angular/core';
import { Business } from '@models/businesses';
import { BusinessClient, ClientType, PersonClient } from '@models/clients';
import {
	Matter,
	PersonMatterClient,
	BusinessMatterClient,
} from '@models/matter';
import { AllAdjustmentsCalculated } from '@state/matter/adjustments/adjustments.selectors';
import { cloneDeep } from 'lodash';
import { BaseFormMapper } from './base-form-mapper';
import { NameFormattingSettings } from '@models/businessSettings/settings';

@Injectable({
	providedIn: 'root',
})
export class ON_GST_524_REBATE_SCHEDULE_Mapper extends BaseFormMapper<ON_GST_524_REBATE_SCHEDULE> {
	CloserToFormMappings = Mappings;

	mergeFormData(
		formData: ON_GST_524_REBATE_SCHEDULE,
		matter: Matter,
		nameFormattingSettings: NameFormattingSettings,
		sellers: (PersonMatterClient | BusinessMatterClient)[],
		buyers: (PersonMatterClient | BusinessMatterClient)[],
		adjustments: AllAdjustmentsCalculated,
		business: Business,
	) {
		const _formData = formData
			? cloneDeep(formData)
			: ({} as ON_GST_524_REBATE_SCHEDULE);

		let primary = buyers?.find((x) => x.primaryContact);
		if (!primary && buyers?.length > 0) {
			primary = buyers[0];
		}

		if (!(_formData?.A_claimantName?.length > 0) && primary) {
			_formData.A_claimantName = this.getClientsName(primary);
		}

		if (
			!(_formData?.A_claimantBusinessNumber1?.length > 0) &&
			(primary as BusinessClient)?.corporationNumber?.length >= 9
		) {
			_formData.A_claimantBusinessNumber1 = (
				primary as BusinessClient
			)?.corporationNumber?.substring(0, 9);
		}

		if (
			!(_formData?.A_claimantBusinessNumber2?.length > 0) &&
			(primary as BusinessClient)?.corporationNumber?.length >= 13
		) {
			_formData.A_claimantBusinessNumber2 = (
				primary as BusinessClient
			)?.corporationNumber?.substring(9, 13);
		}

		return _formData;
	}

	MapValues(): Partial<ON_GST_524_REBATE_SCHEDULE> {
		return {};
	}
}

const Mappings: ON_GST_524_REBATE_SCHEDULE = {
	A_claimantName: 'form1[0].Page1[0].PartA[0].PartA_Name[0]',
	A_claimantBusinessNumber1:
		'form1[0].Page1[0].PartA[0].PartA_BusinessNumber[0].BusinessNumber_RT1[0]',
	A_claimantBusinessNumber2:
		'form1[0].Page1[0].PartA[0].PartA_BusinessNumber[0].BusinessNumber_RT2[0]',
	B_provinceHst: 'form1[0].Page1[0].PartB[0].LineA[0].AmountA[0].Amount[0]',
	B_fairMarketValue: 'form1[0].Page1[0].PartB[0].LineB[0].AmountB[0].Amount[0]',
	B_complexPurchasePrice:
		'form1[0].Page1[0].PartB[0].LineC[0].AmountC[0].Amount[0]',
	B_rebateAmount: 'form1[0].Page1[0].PartB[0].LineD[0].AmountD[0].Amount[0]',
	C_provinceHst: 'form1[0].Page1[0].PartC[0].LineE[0].AmountE[0].Amount[0]',
	C_fairMarketValue: 'form1[0].Page1[0].PartC[0].LineF[0].AmountF[0].Amount[0]',
	C_multiplyProvinceHst:
		'form1[0].Page2[0].PartC[0].LineG[0].AmountG[0].Amount[0]',
	C_salePrice: 'form1[0].Page2[0].PartC[0].LineH[0].AmountH[0].Amount[0]',
	C_multiplySalePrice:
		'form1[0].Page2[0].PartC[0].LineI[0].AmountI[0].Amount[0]',
	C_rebateAmount: 'form1[0].Page2[0].PartC[0].LineJ[0].AmountJ[0].Amount[0]',
	D_provinceHst: 'form1[0].Page2[0].PartD[0].LineK[0].AmountK[0].Amount[0]',
	D_fairMarket: 'form1[0].Page2[0].PartD[0].LineL[0].AmountL[0].Amount[0]',
	D_nonHstPrice: 'form1[0].Page2[0].PartD[0].LineM[0].AmountM[0].Amount[0]',
	D_unitFloorSpace: 'form1[0].Page2[0].PartD[0].LineN[0].LineN[0]',
	D_complexFloorSpace: 'form1[0].Page2[0].PartD[0].LineO[0].LineO[0]',
	D_unitPercFloorSpace: 'form1[0].Page2[0].PartD[0].LineP[0].LineP[0]',
	D_unitFairMarketValue:
		'form1[0].Page2[0].PartD[0].LineQ[0].AmountQ[0].Amount[0]',
	D_R_provinceHst:
		'form1[0].Page2[0].PartD[0].LineR[0].LineRSubform[0].PartD_LineR_Calculation2[0].Amount_LineI[0]',
	D_R_unitPercFloorSpace:
		'form1[0].Page2[0].PartD[0].LineR[0].LineRSubform[0].PartD_LineR_Calculation3[1]',
	D_R_result: 'form1[0].Page2[0].PartD[0].LineR[0].AmountR[0].Amount[0]',
	D_shareOfCapitalStock:
		'form1[0].Page2[0].PartD[0].LineS[0].AmountS[0].Amount[0]',
	D_shareOfCapitalStock_mult:
		'form1[0].Page2[0].PartD[0].LineT[0].AmountT[0].Amount[0]',
	D_rentalPropRebate:
		'form1[0].Page2[0].PartD[0].LineU[0].AmountU[0].Amount[0]',
	E_provinceHst:
		'form1[0].Page3[0].PartE[0].Section1[0].LineV[0].AmountV[0].Amount[0]',
	E_fairMarket:
		'form1[0].Page3[0].PartE[0].Section1[0].LineW[0].AmountW[0].Amount[0]',
	E_complexPurchasePrice:
		'form1[0].Page3[0].PartE[0].Section1[0].LineX[0].AmountX[0].Amount[0]',
	E_provinceHSTMult:
		'form1[0].Page3[0].PartE[0].Section1[0].LineY[0].AmountY[0].Amount[0]',
	E_sqMResidentialUnits:
		'form1[0].Page3[0].PartE[0].Section1[0].LineZ[0].LineZ[0]',
	E_rc_row1_cell1:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row1[0].Cell1[0]',
	E_rc_row1_cell2:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row1[0].Cell2[0]',
	E_rc_row1_cell3:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row1[0].Cell3[0]',
	E_rc_row1_cell4:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row1[0].Cell4[0]',
	E_rc_row1_cell5:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row1[0].Cell5[0]',
	E_rc_row2_cell1:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row2[0].Cell1[0]',
	E_rc_row2_cell2:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row2[0].Cell2[0]',
	E_rc_row2_cell3:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row2[0].Cell3[0]',
	E_rc_row2_cell4:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row2[0].Cell4[0]',
	E_rc_row2_cell5:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row2[0].Cell5[0]',
	E_rc_row3_cell1:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row3[0].Cell1[0]',
	E_rc_row3_cell2:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row3[0].Cell2[0]',
	E_rc_row3_cell3:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row3[0].Cell3[0]',
	E_rc_row3_cell4:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row3[0].Cell4[0]',
	E_rc_row3_cell5:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row3[0].Cell5[0]',
	E_rc_row4_cell1:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row4[0].Cell1[0]',
	E_rc_row4_cell2:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row4[0].Cell2[0]',
	E_rc_row4_cell3:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row4[0].Cell3[0]',
	E_rc_row4_cell4:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row4[0].Cell4[0]',
	E_rc_row4_cell5:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row4[0].Cell5[0]',
	E_rc_row5_cell1:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row5[0].Cell1[0]',
	E_rc_row5_cell2:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row5[0].Cell2[0]',
	E_rc_row5_cell3:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row5[0].Cell3[0]',
	E_rc_row5_cell4:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row5[0].Cell4[0]',
	E_rc_row5_cell5:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row5[0].Cell5[0]',
	E_rc_row6_cell1:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row6[0].Cell1[0]',
	E_rc_row6_cell2:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row6[0].Cell2[0]',
	E_rc_row6_cell3:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row6[0].Cell3[0]',
	E_rc_row6_cell4:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row6[0].Cell4[0]',
	E_rc_row6_cell5:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row6[0].Cell5[0]',
	E_rc_row7_cell1:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row7[0].Cell1[0]',
	E_rc_row7_cell2:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row7[0].Cell2[0]',
	E_rc_row7_cell3:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row7[0].Cell3[0]',
	E_rc_row7_cell4:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row7[0].Cell4[0]',
	E_rc_row7_cell5:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row7[0].Cell5[0]',
	E_rc_row8_cell1:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row8[0].Cell1[0]',
	E_rc_row8_cell2:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row8[0].Cell2[0]',
	E_rc_row8_cell3:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row8[0].Cell3[0]',
	E_rc_row8_cell4:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row8[0].Cell4[0]',
	E_rc_row8_cell5:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row8[0].Cell5[0]',
	E_rc_row9_cell1:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row9[0].Cell1[0]',
	E_rc_row9_cell2:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row9[0].Cell2[0]',
	E_rc_row9_cell3:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row9[0].Cell3[0]',
	E_rc_row9_cell4:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row9[0].Cell4[0]',
	E_rc_row9_cell5:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row9[0].Cell5[0]',
	E_rc_row10_cell1:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row10[0].Cell1[0]',
	E_rc_row10_cell2:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row10[0].Cell2[0]',
	E_rc_row10_cell3:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row10[0].Cell3[0]',
	E_rc_row10_cell4:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row10[0].Cell4[0]',
	E_rc_row10_cell5:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row10[0].Cell5[0]',
	E_rc_row11_cell1:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row11[0].Cell1[0]',
	E_rc_row11_cell2:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row11[0].Cell2[0]',
	E_rc_row11_cell3:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row11[0].Cell3[0]',
	E_rc_row11_cell4:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row11[0].Cell4[0]',
	E_rc_row11_cell5:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row11[0].Cell5[0]',
	E_rc_row12_cell1:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row12[0].Cell1[0]',
	E_rc_row12_cell2:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row12[0].Cell2[0]',
	E_rc_row12_cell3:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row12[0].Cell3[0]',
	E_rc_row12_cell4:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row12[0].Cell4[0]',
	E_rc_row12_cell5:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row12[0].Cell5[0]',
	E_rc_row13_cell1:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row13[0].Cell1[0]',
	E_rc_row13_cell2:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row13[0].Cell2[0]',
	E_rc_row13_cell3:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row13[0].Cell3[0]',
	E_rc_row13_cell4:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row13[0].Cell4[0]',
	E_rc_row13_cell5:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row13[0].Cell5[0]',
	E_rc_row14_cell1:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row14[0].Cell1[0]',
	E_rc_row14_cell2:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row14[0].Cell2[0]',
	E_rc_row14_cell3:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row14[0].Cell3[0]',
	E_rc_row14_cell4:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row14[0].Cell4[0]',
	E_rc_row14_cell5:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row14[0].Cell5[0]',
	E_rc_row15_cell1:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row15[0].Cell1[0]',
	E_rc_row15_cell2:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row15[0].Cell2[0]',
	E_rc_row15_cell3:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row15[0].Cell3[0]',
	E_rc_row15_cell4:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row15[0].Cell4[0]',
	E_rc_row15_cell5:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row15[0].Cell5[0]',
	E_rc_row16_cell1:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row16[0].Cell1[0]',
	E_rc_row16_cell2:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row16[0].Cell2[0]',
	E_rc_row16_cell3:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row16[0].Cell3[0]',
	E_rc_row16_cell4:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row16[0].Cell4[0]',
	E_rc_row16_cell5:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row16[0].Cell5[0]',
	E_rc_row17_cell1:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row17[0].Cell1[0]',
	E_rc_row17_cell2:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row17[0].Cell2[0]',
	E_rc_row17_cell3:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row17[0].Cell3[0]',
	E_rc_row17_cell4:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row17[0].Cell4[0]',
	E_rc_row17_cell5:
		'form1[0].Page3[0].PartE[0].Section2[0].Table1[0].Row17[0].Cell5[0]',
	E_totalResidentialUnits:
		'form1[0].Page4[0].PartE[0].Section3[0].LineAA[0].Section3_Sep_LineAA[0]',
	E_rebateAmount:
		'form1[0].Page4[0].PartE[0].Section3[0].LineBB[0].AmountBB[0].Amount[0]',
	F_hstFairMarketPrice:
		'form1[0].Page4[0].PartF[0].LineCC[0].AmountCC[0].Amount[0]',
	F_fairMarketLand:
		'form1[0].Page4[0].PartF[0].LineDD[0].AmountDD[0].Amount[0]',
	F_hstFairMarketMult:
		'form1[0].Page4[0].PartF[0].LineEE[0].AmountEE[0].Amount[0]',
	F_siteInResPark: 'form1[0].Page4[0].PartF[0].LineFF[0].LineFF[0]',
	F_marketPricePerSite:
		'form1[0].Page4[0].PartF[0].LineGG[0].AmountGG[0].Amount[0]',
	F_marketPricePerSiteMult:
		'form1[0].Page4[0].PartF[0].LineHH[0].AmountHH[0].Amount[0]',
	F_rebateAmount: 'form1[0].Page4[0].PartF[0].LineII[0].AmountII[0].Amount[0]',
};

export interface ON_GST_524_REBATE_SCHEDULE {
	A_claimantName: string;
	A_claimantBusinessNumber1: string;
	A_claimantBusinessNumber2: string;
	B_provinceHst: string;
	B_fairMarketValue: string;
	B_complexPurchasePrice: string;
	B_rebateAmount: string;
	C_provinceHst: string;
	C_fairMarketValue: string;
	C_multiplyProvinceHst: string;
	C_salePrice: string;
	C_multiplySalePrice: string;
	C_rebateAmount: string;
	D_provinceHst: string;
	D_fairMarket: string;
	D_nonHstPrice: string;
	D_unitFloorSpace: string;
	D_complexFloorSpace: string;
	D_unitPercFloorSpace: string;
	D_unitFairMarketValue: string;
	D_R_provinceHst: string;
	D_R_unitPercFloorSpace: string;
	D_R_result: string;
	D_shareOfCapitalStock: string;
	D_shareOfCapitalStock_mult: string;
	D_rentalPropRebate: string;
	E_provinceHst: string;
	E_fairMarket: string;
	E_complexPurchasePrice: string;
	E_provinceHSTMult: string;
	E_sqMResidentialUnits: string;
	E_rc_row1_cell1: string;
	E_rc_row1_cell2: string;
	E_rc_row1_cell3: string;
	E_rc_row1_cell4: string;
	E_rc_row1_cell5: string;
	E_rc_row2_cell1: string;
	E_rc_row2_cell2: string;
	E_rc_row2_cell3: string;
	E_rc_row2_cell4: string;
	E_rc_row2_cell5: string;
	E_rc_row3_cell1: string;
	E_rc_row3_cell2: string;
	E_rc_row3_cell3: string;
	E_rc_row3_cell4: string;
	E_rc_row3_cell5: string;
	E_rc_row4_cell1: string;
	E_rc_row4_cell2: string;
	E_rc_row4_cell3: string;
	E_rc_row4_cell4: string;
	E_rc_row4_cell5: string;
	E_rc_row5_cell1: string;
	E_rc_row5_cell2: string;
	E_rc_row5_cell3: string;
	E_rc_row5_cell4: string;
	E_rc_row5_cell5: string;
	E_rc_row6_cell1: string;
	E_rc_row6_cell2: string;
	E_rc_row6_cell3: string;
	E_rc_row6_cell4: string;
	E_rc_row6_cell5: string;
	E_rc_row7_cell1: string;
	E_rc_row7_cell2: string;
	E_rc_row7_cell3: string;
	E_rc_row7_cell4: string;
	E_rc_row7_cell5: string;
	E_rc_row8_cell1: string;
	E_rc_row8_cell2: string;
	E_rc_row8_cell3: string;
	E_rc_row8_cell4: string;
	E_rc_row8_cell5: string;
	E_rc_row9_cell1: string;
	E_rc_row9_cell2: string;
	E_rc_row9_cell3: string;
	E_rc_row9_cell4: string;
	E_rc_row9_cell5: string;
	E_rc_row10_cell1: string;
	E_rc_row10_cell2: string;
	E_rc_row10_cell3: string;
	E_rc_row10_cell4: string;
	E_rc_row10_cell5: string;
	E_rc_row11_cell1: string;
	E_rc_row11_cell2: string;
	E_rc_row11_cell3: string;
	E_rc_row11_cell4: string;
	E_rc_row11_cell5: string;
	E_rc_row12_cell1: string;
	E_rc_row12_cell2: string;
	E_rc_row12_cell3: string;
	E_rc_row12_cell4: string;
	E_rc_row12_cell5: string;
	E_rc_row13_cell1: string;
	E_rc_row13_cell2: string;
	E_rc_row13_cell3: string;
	E_rc_row13_cell4: string;
	E_rc_row13_cell5: string;
	E_rc_row14_cell1: string;
	E_rc_row14_cell2: string;
	E_rc_row14_cell3: string;
	E_rc_row14_cell4: string;
	E_rc_row14_cell5: string;
	E_rc_row15_cell1: string;
	E_rc_row15_cell2: string;
	E_rc_row15_cell3: string;
	E_rc_row15_cell4: string;
	E_rc_row15_cell5: string;
	E_rc_row16_cell1: string;
	E_rc_row16_cell2: string;
	E_rc_row16_cell3: string;
	E_rc_row16_cell4: string;
	E_rc_row16_cell5: string;
	E_rc_row17_cell1: string;
	E_rc_row17_cell2: string;
	E_rc_row17_cell3: string;
	E_rc_row17_cell4: string;
	E_rc_row17_cell5: string;
	E_totalResidentialUnits: string;
	E_rebateAmount: string;
	F_hstFairMarketPrice: string;
	F_fairMarketLand: string;
	F_hstFairMarketMult: string;
	F_siteInResPark: string;
	F_marketPricePerSite: string;
	F_marketPricePerSiteMult: string;
	F_rebateAmount: string;
}
